import { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { CompanyProvider } from "@/context/CompanyContext";
import { SubscriptionProvider } from "@/context/SubscriptionContext";
import Login from "@/pages/Login";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import Profile from "@/pages/Profile";
import AuditLog from "@/pages/AuditLog";
import Subscribe from "@/pages/Subscribe";
import AppShell from "@/components/AppShell";
import Dashboard from "@/pages/Dashboard";
import Companies from "@/pages/Companies";
import Ledgers from "@/pages/Ledgers";
import Vouchers from "@/pages/Vouchers";
import VoucherEntry from "@/pages/VoucherEntry";
import Inventory from "@/pages/Inventory";
import Styles from "@/pages/Styles";
import Reports from "@/pages/Reports";
import TrialBalance from "@/pages/reports/TrialBalance";
import ProfitLoss from "@/pages/reports/ProfitLoss";
import BalanceSheet from "@/pages/reports/BalanceSheet";
import CashFlow from "@/pages/reports/CashFlow";
import StockSummary from "@/pages/reports/StockSummary";
import ItemLedger from "@/pages/reports/ItemLedger";
import Orders from "@/pages/Orders";
import OrderForm from "@/pages/OrderForm";
import BankStatementImport from "@/pages/BankStatementImport";
import BankImportRecommend from "@/pages/BankImportRecommend";
import BankReconDashboard from "@/pages/BankReconDashboard";
import BankSmartSettings from "@/pages/BankSmartSettings";
import AdminSubscriptions from "@/pages/AdminSubscriptions";
import DayBook from "@/pages/reports/DayBook";
import GSTSummary from "@/pages/reports/GSTSummary";
import GSTR1 from "@/pages/reports/GSTR1";
import GSTR3B from "@/pages/reports/GSTR3B";
import Banking from "@/pages/Banking";
import BankReconciliation from "@/pages/BankReconciliation";
import ChequeRegister from "@/pages/ChequeRegister";
import ChequePrint from "@/pages/ChequePrint";
import Payroll from "@/pages/Payroll";
import Employees from "@/pages/payroll/Employees";
import PayRun from "@/pages/payroll/PayRun";
import Payslips from "@/pages/payroll/Payslips";
import PayslipPrint from "@/pages/payroll/PayslipPrint";
import Outstanding from "@/pages/reports/Outstanding";
import AllLedgers from "@/pages/reports/AllLedgers";
import LedgerStatement from "@/pages/reports/LedgerStatement";
import Backup from "@/pages/Backup";
import BulkVouchers from "@/pages/BulkVouchers";
import BulkPurchaseVouchers from "@/pages/BulkPurchaseVouchers";
import AIVoucherEntry from "@/pages/AIVoucherEntry";
import PrivacyPolicy from "@/pages/PrivacyPolicy";
import TaxDashboard from "@/pages/tax/TaxDashboard";
import TaxpayerProfile from "@/pages/tax/TaxpayerProfile";
import DepreciationBlocks from "@/pages/tax/DepreciationBlocks";
import TaxComputation from "@/pages/tax/TaxComputation";
import TaxWorkingPaper from "@/pages/tax/TaxWorkingPaper";
import TaxLosses from "@/pages/tax/TaxLosses";
import AdvanceTaxPlanner from "@/pages/tax/AdvanceTaxPlanner";
import TaxImports from "@/pages/tax/TaxImports";
import CapitalGains from "@/pages/tax/CapitalGains";
import AgriIncome from "@/pages/tax/AgriIncome";
import Clubbing from "@/pages/tax/Clubbing";
import SalaryIncome from "@/pages/tax/SalaryIncome";
import HouseProperty from "@/pages/tax/HouseProperty";
import FilingAndPayments from "@/pages/tax/FilingAndPayments";
import IncomeTaxPortal from "@/pages/tax/IncomeTaxPortal";
import { TaxYearLayout } from "@/context/TaxYearContext";
import InstallPwaPrompt from "@/components/InstallPwaPrompt";

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center text-gray-500 text-sm">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

function AppRoutes() {
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/privacy" element={<PrivacyPolicy />} />
      <Route path="/privacy-policy" element={<PrivacyPolicy />} />
      <Route path="/subscribe" element={<ProtectedRoute><Subscribe /></ProtectedRoute>} />
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <AppShell />
          </ProtectedRoute>
        }
      >
        <Route index element={<Dashboard />} />
        <Route path="companies" element={<Companies />} />
        <Route path="ledgers" element={<Ledgers />} />
        <Route path="vouchers" element={<Vouchers />} />
        <Route path="vouchers/new/:type" element={<VoucherEntry />} />
        <Route path="vouchers/edit/:vid" element={<VoucherEntry />} />
        <Route path="vouchers/bulk" element={<BulkVouchers />} />
        <Route path="vouchers/bulk-purchase" element={<BulkPurchaseVouchers />} />
        <Route path="ai-voucher" element={<AIVoucherEntry />} />
        <Route path="inventory" element={<Inventory />} />
        <Route path="styles" element={<Styles />} />
        <Route path="reports" element={<Reports />} />
        <Route path="reports/trial-balance" element={<TrialBalance />} />
        <Route path="reports/profit-loss" element={<ProfitLoss />} />
        <Route path="reports/balance-sheet" element={<BalanceSheet />} />
        <Route path="reports/cash-flow" element={<CashFlow />} />
        <Route path="reports/stock-summary" element={<StockSummary />} />
        <Route path="reports/item-ledger" element={<ItemLedger />} />
        <Route path="reports/item-ledger/:iid" element={<ItemLedger />} />
        <Route path="orders" element={<Orders />} />
        <Route path="orders/new" element={<OrderForm />} />
        <Route path="orders/:oid/edit" element={<OrderForm />} />
        <Route path="banking/import-statement" element={<BankStatementImport />} />
        <Route path="banking/import-recommend" element={<BankImportRecommend />} />
        <Route path="banking/recon-dashboard" element={<BankReconDashboard />} />
        <Route path="banking/smart-settings" element={<BankSmartSettings />} />
        <Route path="banking/rules" element={<BankSmartSettings />} />
        <Route path="admin/subscriptions" element={<AdminSubscriptions />} />
        <Route path="reports/day-book" element={<DayBook />} />
        <Route path="reports/gst-summary" element={<GSTSummary />} />
        <Route path="reports/gstr1" element={<GSTR1 />} />
        <Route path="reports/gstr3b" element={<GSTR3B />} />
        <Route path="reports/outstanding" element={<Outstanding />} />
        <Route path="reports/all-ledgers" element={<AllLedgers />} />
        <Route path="reports/ledger/:lid" element={<LedgerStatement />} />
        <Route path="settings/profile" element={<Profile />} />
        <Route path="settings/audit-log" element={<AuditLog />} />
        <Route path="banking" element={<Banking />} />
        <Route path="banking/reconciliation" element={<BankReconciliation />} />
        <Route path="banking/cheques" element={<ChequeRegister />} />
        <Route path="banking/cheques/:vid/print" element={<ChequePrint />} />
        <Route path="payroll" element={<Payroll />} />
        <Route path="payroll/employees" element={<Employees />} />
        <Route path="payroll/pay-run" element={<PayRun />} />
        <Route path="payroll/payslips" element={<Payslips />} />
        <Route path="payroll/payslips/:psid/print" element={<PayslipPrint />} />
        <Route path="backup" element={<Backup />} />
        <Route element={<TaxYearLayout />}>
          <Route path="tax" element={<TaxDashboard />} />
          <Route path="tax/profile" element={<TaxpayerProfile />} />
          <Route path="tax/depreciation" element={<DepreciationBlocks />} />
          <Route path="tax/losses" element={<TaxLosses />} />
          <Route path="tax/advance-tax" element={<AdvanceTaxPlanner />} />
          <Route path="tax/imports" element={<TaxImports />} />
          <Route path="tax/filing" element={<FilingAndPayments />} />
          <Route path="tax/portal" element={<IncomeTaxPortal />} />
          <Route path="tax/computation/:tcid" element={<TaxComputation />} />
          <Route path="tax/computation/:tcid/print" element={<TaxWorkingPaper />} />
          <Route path="tax/capital-gains" element={<CapitalGains />} />
          <Route path="tax/agri-income" element={<AgriIncome />} />
          <Route path="tax/clubbing" element={<Clubbing />} />
          <Route path="tax/salary" element={<SalaryIncome />} />
          <Route path="tax/house-property" element={<HouseProperty />} />
        </Route>
      </Route>
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <SubscriptionProvider>
          <CompanyProvider>
            <Toaster position="top-right" richColors />
            <AppRoutes />
            <InstallPwaPrompt />
          </CompanyProvider>
        </SubscriptionProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
